/**
 * Şifre değiştirme formu — hem zorunlu değişiklik SAYFASI hem profil menüsünden açılan
 * POPUP bunu kullanıyor. İki kopya olsaydı doğrulama kuralları ayrışırdı.
 */
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/shared/api/http/client';
import { ENDPOINTS } from '@/shared/api/endpoints';
import { useAuthStore } from '@/entities/session/model/authStore';
import { Button } from '@/shared/ui/Button';
import { PasswordInput } from '@/shared/ui/PasswordInput';
import { Label } from '@/shared/ui/Label';
import { Alert } from '@/shared/ui/Alert';

interface Values {
  current: string;
  next: string;
  confirm: string;
}

export interface ChangePasswordFormProps {
  /** Başarıyla kaydedildiğinde: sayfa ana ekrana gider, popup kendini kapatır. */
  onSaved: () => void;
  /** Popup'ta ikinci bir düğme (Vazgeç) gösterilir. */
  onCancel?: () => void;
  idPrefix?: string;
}

export function ChangePasswordForm({ onSaved, onCancel, idPrefix = 'pwd' }: ChangePasswordFormProps) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const clearFlag = useAuthStore((s) => s.clearPasswordChangeFlag);
  const [error, setError] = useState<string | null>(null);
  const form = useForm<Values>();

  async function onSubmit(v: Values) {
    setError(null);
    if (v.next !== v.confirm) {
      setError(t('auth.changePassword.mismatch'));
      return;
    }
    try {
      await apiClient.post(
        ENDPOINTS.SYSTEM_CHANGE_PASSWORD,
        { UserId: user?.userId, UserCode: user?.userCode, CurrentPassword: v.current, NewPassword: v.next },
        { octo: { expectSuccess: true } },
      );
      clearFlag();
      form.reset();
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
      <div className="space-y-1">
        <Label htmlFor={`${idPrefix}-cur`}>{t('auth.changePassword.current')}</Label>
        <PasswordInput
          id={`${idPrefix}-cur`}
          autoComplete="current-password"
          {...form.register('current', { required: true })}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor={`${idPrefix}-new`}>{t('auth.changePassword.new')}</Label>
        <PasswordInput
          id={`${idPrefix}-new`}
          autoComplete="new-password"
          {...form.register('next', { required: true, minLength: 6 })}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor={`${idPrefix}-conf`}>{t('auth.changePassword.confirm')}</Label>
        <PasswordInput
          id={`${idPrefix}-conf`}
          autoComplete="new-password"
          {...form.register('confirm', { required: true })}
        />
      </div>
      {error ? <Alert tone="danger">{error}</Alert> : null}
      <div className="flex gap-2">
        {onCancel ? (
          <Button type="button" variant="secondary" className="flex-1" onClick={onCancel}>
            {t('common:Cancel')}
          </Button>
        ) : null}
        <Button type="submit" className="flex-1" loading={form.formState.isSubmitting}>
          {t('auth.changePassword.save')}
        </Button>
      </div>
    </form>
  );
}
